import React, { useState, useEffect } from 'react';
import { Calculator, Users, MousePointer, Percent } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Helmet } from 'react-helmet-async';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const ConversionRateCalculator = () => {
  const [visitors, setVisitors] = useState(1000);
  const [conversions, setConversions] = useState(50);
  const [conversionRate, setConversionRate] = useState(0);

  useEffect(() => {
    calculateConversionRate();
  }, [visitors, conversions]);

  const calculateConversionRate = () => {
    const rate = (conversions / visitors) * 100;
    setConversionRate(parseFloat(rate.toFixed(2)));
  };

  const chartData = {
    labels: ['Current', 'Goal 1', 'Goal 2', 'Goal 3'],
    datasets: [
      {
        label: 'Conversion Rate',
        data: [conversionRate, conversionRate * 1.1, conversionRate * 1.2, conversionRate * 1.3],
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
      },
    ],
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/conversion-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Conversion Rate Calculator: Free Tool to Measure & Optimize Conversions</title>
        <meta name="description" content="Calculate your website's conversion rate instantly with our free calculator. Track visitor-to-customer conversion rates and get optimization tips to boost your success rate." />
        <meta name="keywords" content="conversion rate calculator, website conversion calculator, sales conversion calculator, landing page optimization, conversion optimization tools, marketing metrics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Conversion Rate Calculator: Boost Your Website Performance" />
        <meta property="og:description" content="Calculate and optimize your conversion rates instantly. Get actionable insights to improve your website's performance with our free calculator." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Conversion Rate Calculator: Boost Your Website Performance" />
        <meta property="twitter:description" content="Calculate and optimize your conversion rates instantly. Get actionable insights to improve your website's performance with our free calculator." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Conversion Rate Calculator",
            "description": "Free calculator to measure and optimize website conversion rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Conversion Rate",
              "Visitor tracking",
              "Conversion tracking",
              "Performance projection",
              "Optimization tips"
            ],
            "about": {
              "@type": "Thing",
              "name": "Conversion Rate",
              "description": "The percentage of website visitors who complete a desired action, such as making a purchase or signing up for a newsletter."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Digital Marketers, Business Owners, Website Managers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Conversion Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter the total number of website visitors"
                },
                {
                  "@type": "HowToStep",
                  "text": "Enter the number of conversions"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your calculated conversion rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Conversion Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Number of Visitors</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={visitors}
                  onChange={(e) => setVisitors(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Number of Conversions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MousePointer className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={conversions}
                  onChange={(e) => setConversions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="50"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {conversionRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Conversion Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Conversion Rate Projection</h2>
        <Line data={chartData} />
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Best Practices</h2>
        <p className="text-gray-600">
          Your current conversion rate is {conversionRate}%. This means that out of every 100 visitors to your website or landing page, {conversionRate} are taking the desired action (e.g., making a purchase, signing up for a newsletter).
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Improving Your Conversion Rate</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize your landing page design and copy</li>
          <li>Improve your call-to-action buttons</li>
          <li>Implement A/B testing to find the best-performing elements</li>
          <li>Enhance your website's loading speed</li>
          <li>Provide social proof and testimonials</li>
          <li>Simplify your conversion funnel</li>
          <li>Use persuasive content and clear value propositions</li>
          <li>Optimize for mobile devices</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>E-commerce: 1-4%</li>
          <li>B2B: 2-5%</li>
          <li>SaaS: 3-5%</li>
          <li>Lead Generation: 3-9%</li>
          <li>Email Marketing: 15-25%</li>
        </ul>
      </div>
    </div>
  );
};

export default ConversionRateCalculator;
